import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const LS_KEY = 'rostery_feature_settings';

const DEFAULTS = {
  enable_emar: false,
  enable_quotes: true,
  quote_title: 'Quote',
  add_quote_terms: true,
  default_quote_terms: 'The pricing in this quote is based on the NDIS Pricing Arrangements and Price Limits current at the time of quoting. Services will be invoiced in accordance with the NDIS price limits applicable at the time the service is delivered.',
  enable_tasks: true,
  task_durations: [],
  task_support_categories: [],
  task_invoice_format: '',
  require_support_category: false,
  task_types: [],
  enable_incidents: true,
  enable_incidents_carers_app: true,
  enable_facilities: true,
  publish_shifts: true,
  advanced_break_tracking: false,
  offline_clocking: false,
  enable_general_chat: false,
  enable_team_chats: false,
  enable_direct_messaging: false,
  enable_care_signal_alerts_chat: false,
  // Page/navigation toggles — org admins can hide pages they don't use. All
  // default true so existing orgs are unchanged until they turn one off.
  enable_templates: true,
  enable_groups: true,
  enable_teams: true,
  enable_forms: true,
  enable_invoices: true,
  enable_timesheets: true,
  enable_payslips: true,
  enable_pay_contractors: true,
  enable_reimbursements: true,
  enable_mileage: true,
  enable_financials: true,
  enable_reports: true,
  enable_market_intel: true,
  enable_fleet: true,
  enable_activity_log: true,
};

function fromStorage() {
  try {
    const s = localStorage.getItem(LS_KEY);
    if (s) return { ...DEFAULTS, ...JSON.parse(s) };
  } catch {}
  return DEFAULTS;
}

// Map a super-admin per-org module → the app feature flags it controls, so the
// existing Sidebar `features.enable_*` checks automatically respect org gating.
const MODULE_TO_FEATURES = {
  medications: ['enable_emar'],
  incidents:   ['enable_incidents', 'enable_incidents_carers_app'],
  messages:    ['enable_general_chat', 'enable_team_chats', 'enable_direct_messaging', 'enable_care_signal_alerts_chat'],
};

const FeatureContext = createContext({ features: DEFAULTS, modules: {}, reload: () => {} });

export function FeatureProvider({ children }) {
  const [features, setFeatures] = useState(fromStorage);
  const [modules, setModules]   = useState({});

  const applyModuleGates = (feats, mods) => {
    const merged = { ...feats };
    Object.entries(MODULE_TO_FEATURES).forEach(([mod, keys]) => {
      if (mods && mods[mod] === false) keys.forEach(k => { merged[k] = false; });
    });
    return merged;
  };

  const load = () => {
    // Don't attempt without a token — would cause a 401 redirect loop on the login page
    if (!localStorage.getItem('token')) return;
    // 1) App config features (quotes, task types, chat toggles, etc.)
    api.get('/settings')
      .then(r => {
        const fs = r.data?.settings?.feature_settings;
        if (fs && typeof fs === 'object') {
          localStorage.setItem(LS_KEY, JSON.stringify(fs));
          setFeatures(prev => applyModuleGates({ ...DEFAULTS, ...fs }, modules));
        }
      })
      .catch(() => {});
    // 2) Per-organisation module access (granted by super admin)
    api.get('/features/me')
      .then(r => {
        const mods = r.data?.modules || {};
        setModules(mods);
        setFeatures(prev => applyModuleGates(prev, mods));
      })
      .catch(() => {});
  };

  useEffect(() => {
    load();
    // Re-check feature/module grants when the user returns to the tab, so a
    // super-admin grant (e.g. AI Receptionist) shows up without a full re-login.
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return (
    <FeatureContext.Provider value={{ features, modules, reload: load }}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatures() {
  return useContext(FeatureContext).features;
}

export function useModules() {
  return useContext(FeatureContext).modules;
}

export function useFeatureReload() {
  return useContext(FeatureContext).reload;
}

// Call this directly after a successful save so localStorage stays in sync
export function persistFeatures(cfg) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(cfg)); } catch {}
}
